import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { ISessionData, IUserProfile } from "@miplatform/types";
import { appStorage } from "@miplatform/utils";

type UserStoreState = {
  isAuthenticated: boolean;
  session: ISessionData;
  permissions: string[];
  roleIds?: string[];
  profile: IUserProfile | null;
};

type UserStoreActions = {
  hasPermission: (permission: string) => boolean;
  hasSomePermissions: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setSession: (session: ISessionData) => void;
  setPermissions: (permissions: string[]) => void;
  setRoleIds: (roleIds: string[]) => void;
  getProfile: () => Promise<void>;
};

type UserStore = UserStoreState & UserStoreActions;

const initialState: UserStoreState = {
  isAuthenticated: false,
  session: { tenantId: null, userId: null },
  permissions: [],
  roleIds: [],
  profile: null,
};

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      hasPermission: (permission: string): boolean => {
        return get().permissions.includes(permission);
      },

      hasSomePermissions: (permissions: string[]): boolean => {
        return permissions.some((permission) =>
          get().permissions.includes(permission)
        );
      },

      hasAllPermissions: (permissions: string[]): boolean => {
        if (!Array.isArray(permissions) || permissions.length === 0) {
          return false;
        }
        return permissions.every((permission) =>
          get().permissions.includes(permission)
        );
      },

      setIsAuthenticated: (isAuthenticated: boolean) => {
        set({ isAuthenticated });
      },

      setSession: (session: ISessionData) => {
        set({ session });
      },

      setRoleIds: (roleIds: string[]) => {
        appStorage.setItem("roleIds", roleIds.join(","));
        set({ roleIds });
      },

      setPermissions: (permissions: string[]) => {
        appStorage.setItem("permissions", permissions.join(","));
        set({ permissions });
      },

      getProfile: async () => {
        const {
          session: { userId },
        } = get();

        if (userId) {
          const response = await subscriptionService.users.get(userId);
          if (response.status === HTTP_STATUSES.OK) {
            set({ profile: response.data });
          }
        }
      },
    }),
    {
      name: "userStore",
      storage: createJSONStorage(() => appStorage),
      partialize: (state) => ({
        session: state.session,
        permissions: state.permissions,
        profile: state.profile,
        roleIds: state.roleIds,
      }),
    }
  )
);

// Export initial state for testing
export const UserStoreInitialState = initialState;
